import { create } from 'zustand';
import type { MediaAsset, MediaHealth, MediaKind, MediaStatus } from '@ai-media/shared';
import { api, apiErrorMessage, type Paginated } from '@/lib/api';
import { asArray, asNumber } from '@/lib/collections';
import { toast } from './ui.store';
import { hasPendingAssets } from '@/lib/media';

export interface MediaFilters {
  kind?: MediaKind;
  status?: MediaStatus;
  search?: string;
  page: number;
}

export interface GenerateImageInput {
  prompt: string;
  aspectRatio?: string;
  steps?: number;
  count?: number;
  postId?: string;
  provider?: string;
  model?: string;
}

export interface GenerateVideoInput {
  prompt: string;
  aspectRatio?: string;
  durationSeconds?: number;
  steps?: number;
  postId?: string;
  provider?: string;
  model?: string;
}

interface MediaState {
  assets: MediaAsset[];
  total: number;
  loading: boolean;
  generating: '' | 'image' | 'video';
  filters: MediaFilters;
  health: MediaHealth | null;
  healthLoading: boolean;
  /** Id of the asset currently being attached, for per-card spinners. */
  attaching: string | null;
  load: (patch?: Partial<MediaFilters>) => Promise<void>;
  setFilters: (patch: Partial<MediaFilters>) => void;
  refreshHealth: () => Promise<void>;
  generateImage: (input: GenerateImageInput) => Promise<MediaAsset[]>;
  generateVideo: (input: GenerateVideoInput) => Promise<MediaAsset | null>;
  remove: (id: string) => Promise<void>;
  attach: (assetId: string, postId: string) => Promise<boolean>;
  detach: (assetId: string, postId: string) => Promise<void>;
}

const POLL_INTERVAL_MS = 5_000;
let pollTimer: ReturnType<typeof setTimeout> | null = null;

const defaultFilters: MediaFilters = { page: 1 };

export const useMediaStore = create<MediaState>((set, get) => ({
  assets: [],
  total: 0,
  loading: false,
  generating: '',
  filters: defaultFilters,
  health: null,
  healthLoading: false,
  attaching: null,

  setFilters: (patch) => set((state) => ({ filters: { ...state.filters, ...patch, page: 1 } })),

  load: async (patch) => {
    const filters = { ...get().filters, ...patch };
    set({ filters, loading: true });
    try {
      const { data } = await api.get<Paginated<MediaAsset>>('/media/assets', {
        params: {
          kind: filters.kind,
          status: filters.status,
          search: filters.search,
          page: filters.page,
          limit: 24,
        },
      });
      set({ assets: asArray(data?.data), total: asNumber(data?.total) });
      schedulePoll(() => void get().load());
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      set({ loading: false });
    }
  },

  refreshHealth: async () => {
    set({ healthLoading: true });
    try {
      const { data } = await api.get<MediaHealth>('/media/health');
      set({ health: data });
    } catch (error) {
      set({ health: null });
      toast(apiErrorMessage(error), 'warning');
    } finally {
      set({ healthLoading: false });
    }
  },

  generateImage: async (input) => {
    set({ generating: 'image' });
    try {
      const { data } = await api.post<{ assets: MediaAsset[]; degraded: boolean }>('/media/images', {
        prompt: input.prompt,
        aspectRatio: input.aspectRatio,
        steps: input.steps,
        count: input.count,
        postId: input.postId,
        provider: input.provider,
        model: input.model,
      });
      const created = asArray(data?.assets);
      set((state) => ({
        assets: [...created, ...state.assets],
        total: state.total + created.length,
      }));
      toast(
        data?.degraded
          ? 'GPU worker offline — placeholder artwork saved to your library'
          : `Image ready${created.length > 1 ? ` (${created.length})` : ''}`,
        data?.degraded ? 'warning' : 'success'
      );
      return created;
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
      return [];
    } finally {
      set({ generating: '' });
    }
  },

  generateVideo: async (input) => {
    set({ generating: 'video' });
    try {
      const { data } = await api.post<{ asset: MediaAsset; queued: boolean }>('/media/videos', {
        prompt: input.prompt,
        aspectRatio: input.aspectRatio,
        durationSeconds: input.durationSeconds,
        steps: input.steps,
        postId: input.postId,
        provider: input.provider,
        model: input.model,
      });
      const asset = data?.asset;
      if (!asset) {
        toast('The render finished without returning an asset', 'warning');
        return null;
      }
      set((state) => ({ assets: [asset, ...state.assets], total: state.total + 1 }));
      toast(
        data?.queued
          ? 'Video render queued — it appears here as soon as the GPU finishes'
          : 'GPU worker offline — a placeholder clip was saved instead',
        data?.queued ? 'success' : 'warning'
      );
      if (data?.queued) schedulePoll(() => void get().load());
      return asset;
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
      return null;
    } finally {
      set({ generating: '' });
    }
  },

  remove: async (id) => {
    try {
      await api.delete(`/media/assets/${id}`);
      set((state) => ({
        assets: state.assets.filter((asset) => asset.id !== id),
        total: Math.max(0, state.total - 1),
      }));
      toast('Asset removed', 'medium');
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  },

  attach: async (assetId, postId) => {
    set({ attaching: assetId });
    try {
      const { data } = await api.post<{ asset: MediaAsset; added: boolean }>(
        `/media/assets/${assetId}/attach`,
        { postId }
      );
      const asset = data?.asset;
      if (asset) {
        set((state) => ({
          assets: state.assets.map((item) => (item.id === asset.id ? asset : item)),
        }));
      }
      toast(
        data?.added ? 'Media attached to the post' : 'Media is already on this post',
        data?.added ? 'success' : 'medium'
      );
      return Boolean(data?.added);
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
      return false;
    } finally {
      set({ attaching: null });
    }
  },

  detach: async (assetId, postId) => {
    try {
      await api.delete(`/media/assets/${assetId}/attach/${postId}`);
      toast('Media removed from the post', 'medium');
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  },
}));

/**
 * While a render is in flight the list is re-fetched on a timer so a card flips
 * from `processing` to `ready` without a manual refresh.
 */
function schedulePoll(run: () => void): void {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  if (!hasPendingAssets(useMediaStore.getState().assets)) return;
  pollTimer = setTimeout(() => {
    pollTimer = null;
    run();
  }, POLL_INTERVAL_MS);
}

export function stopMediaPolling(): void {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}
